import toast from "react-hot-toast";
import {
  Paperclip,
  Plus,
  Edit2,
  Trash2,
  File,
  User,
  Eye,
  Search,
  Download,
} from "lucide-react";
import { useState } from "react";
import ConfirmModal from "../modal/ConfirmModal";
import AttachmentModal from "../modal/AttachmentModal";
import { useAttachments } from "../hooks/useAttachments";
import { useUsers } from "../hooks/useUsers";

const AttachmentManager = () => {
  const { users } = useUsers();

  const {
    attachments,
    addAttachment,
    updateAttachment,
    deleteAttachment,
    loading,
    error,
  } = useAttachments();

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);

  const filteredAttachments = attachments.filter((attachment) => {
    const term = searchTerm.toLowerCase();
    const nameMatch = attachment.name?.toLowerCase().includes(term);
    const fileMatch = attachment.fileName?.toLowerCase().includes(term);
    const userMatch = attachment.users
      ?.map((a) => a.name.toLowerCase())
      .join(" ")
      .includes(term);

    return nameMatch || fileMatch || userMatch;
  });

  const openAttachmentModal = () => {
    setSelectedAttachment(null);
    setIsModalOpen(true);
  };

  const handleSaveAttachment = async (attachmentData) => {
    try {
      if (selectedAttachment) {
        await updateAttachment(selectedAttachment.id, attachmentData);
        toast.success("Anexo atualizado com sucesso!");
      } else {
        await addAttachment(attachmentData);
        toast.success("Anexo criado com sucesso!");
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao salvar o anexo.");
    }
  };

  const handleEditAttachment = (attachment) => {
    setSelectedAttachment(attachment);
    setIsModalOpen(true);
  };

  const handleDeleteAttachment = (attachmentId) => {
    setAttachmentToDelete(attachmentId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (attachmentToDelete) {
        await deleteAttachment(attachmentToDelete);
        toast.success("Anexo excluído com sucesso!");
        setAttachmentToDelete(null);
        setConfirmOpen(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir o anexo.");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  if (loading)
    return (
      <p className="p-6 text-gray-700 dark:text-gray-300">
        Carregando anexos...
      </p>
    );
  if (error) return <p className="text-red-500">Erro: {error}</p>;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        {/* Cabeçalho */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestão de Anexos
          </h1>
          <button
            onClick={openAttachmentModal}
            className="flex items-center rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Anexo
          </button>
        </div>

        {/* Estatísticas */}
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <File className="mx-auto mb-2 h-8 w-8 text-blue-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {attachments.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total de Anexos
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <User className="mx-auto mb-2 h-8 w-8 text-green-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {new Set(attachments.map((a) => a.userId)).size}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Usuários com Anexos
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <Paperclip className="mx-auto mb-2 h-8 w-8 text-yellow-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatFileSize(
                attachments.reduce((sum, a) => sum + (a.fileSize || 0), 0),
              )}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Tamanho Total
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <Edit2 className="mx-auto mb-2 h-8 w-8 text-purple-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {attachments.filter((a) => a.updatedAt).length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Anexos Editados
            </p>
          </div>
        </div>

        {/* Tabela de Anexos */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-colors duration-300 dark:border-gray-700 dark:bg-gray-900">
          {/* Barra de busca */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Lista de Anexos
            </h2>

            <div className="relative w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Arquivo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Usuário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Tamanho
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Criado em
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {filteredAttachments.map((attachment) => (
                  <tr
                    key={attachment.id}
                    className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {/* Nome do anexo */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {attachment.name}
                      </span>
                    </td>

                    {/* Arquivo */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                        <Paperclip className="h-4 w-4 text-gray-500" />
                        <span
                          className="max-w-[140px] truncate"
                          title={attachment.fileName}
                        >
                          {attachment.fileName.length > 20
                            ? attachment.fileName.slice(0, 20) + "..."
                            : attachment.fileName}
                        </span>

                        {/* Botões de ação */}
                        <div className="flex items-center space-x-2">
                          {/* Visualizar */}
                          <a
                            href={`${process.env.REACT_APP_URL}${attachment.filePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-600"
                            title="Visualizar"
                          >
                            <Eye className="h-4 w-4" />
                          </a>

                          {/* Download */}
                          <a
                            href={`${process.env.REACT_APP_URL}${attachment.filePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={attachment.fileName}
                            className="text-green-500 hover:text-green-600"
                            title="Baixar"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </div>
                      </div>
                    </td>

                    {/* Users */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {attachment.users && attachment.users.length > 0
                        ? attachment.users.map((a) => a.name).join(", ")
                        : "-"}
                    </td>

                    {/* Tamanho */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {formatFileSize(attachment.fileSize)}
                    </td>

                    {/* Data */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                      {attachment.createdAt
                        ? new Date(attachment.createdAt).toLocaleDateString()
                        : "-"}
                    </td>

                    {/* Ações (editar / excluir) */}
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditAttachment(attachment)}
                          className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {attachments.length === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Nenhum anexo encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modais */}
      <AttachmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAttachment}
        attachment={selectedAttachment}
        users={users}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Excluir Anexo"
        message="Tem certeza que deseja excluir este anexo? Esta ação não pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default AttachmentManager;
